// Factoring: agrupa depósitos del mismo origen y los concilia contra el pago Alegra existente (Iter 355)
import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { fmtFecha } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Loader2, CheckCircle2, HandCoins } from 'lucide-react';
import { toast } from 'sonner';

const CLP = (v) => (v || 0).toLocaleString('es-CL');

export function FactoringDialog({ open, onClose, mov, onDone }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [contraparte, setContraparte] = useState(null);
  const [pagoSel, setPagoSel] = useState(null);
  const [nota, setNota] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!open || !mov) return;
    setData(null); setSel([]); setContraparte(null); setPagoSel(null); setNota('');
    api.get('/conciliacion-bancaria/factoring/candidatos', { params: { mov_id: mov.id } }).then((r) => {
      setData(r.data);
      setSel(r.data.hermanos.map((h) => h.id));
      if (r.data.contrapartes.length === 1) setContraparte(r.data.contrapartes[0]);
      const calza = r.data.pagos_alegra.find((p) => p.calza_total && !p.ya_usado);
      if (calza) setPagoSel(calza.alegra_id);
    }).catch((e) => toast.error(e.response?.data?.detail || 'Error buscando candidatos'));
  }, [open, mov]);

  const total = (data?.hermanos || []).filter((h) => sel.includes(h.id)).reduce((s, h) => s + h.abono, 0);
  const pago = (data?.pagos_alegra || []).find((p) => p.alegra_id === pagoSel);
  const toggle = (id) => setSel((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const confirmar = async () => {
    if (!contraparte) { toast.error('Selecciona la contraparte (empresa de factoring)'); return; }
    if (!pagoSel) { toast.error('Selecciona el pago Alegra que respalda el anticipo'); return; }
    setGuardando(true);
    try {
      const r = await api.post('/conciliacion-bancaria/factoring', { mov_ids: sel, contraparte_alegra_id: String(contraparte.alegra_id), alegra_payment_id: pagoSel, nota });
      toast.success(r.data.message);
      onDone?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error registrando el factoring'); }
    finally { setGuardando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="dialog-factoring">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><HandCoins className="h-5 w-5 text-violet-600" /> Conciliar como factoring / financiamiento</DialogTitle></DialogHeader>
        {!data ? <div className="py-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div> : (
          <div className="space-y-3 text-sm">
            <div>
              <p className="mb-1 text-xs font-medium">Depósitos del mismo origen (±3 días) — {sel.length} seleccionados · <b>${CLP(total)}</b></p>
              <div className="max-h-40 space-y-1 overflow-y-auto" data-testid="factoring-hermanos">
                {data.hermanos.map((h) => (
                  <label key={h.id} className={`flex cursor-pointer items-center gap-2 rounded border p-1.5 text-xs ${sel.includes(h.id) ? 'border-violet-400 bg-violet-50' : ''}`}>
                    <input type="checkbox" checked={sel.includes(h.id)} onChange={() => toggle(h.id)} data-testid={`factoring-dep-${h.id}`} />
                    <span className="flex-1">{fmtFecha(h.fecha)} · {h.detalle}</span><span className="font-mono">${CLP(h.abono)}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium">Contraparte (empresa de factoring, contacto Alegra)</p>
              {data.contrapartes.length === 0 ? <p className="text-xs text-red-600">No encontré un contacto Alegra con ese nombre; créalo en Alegra y sincroniza.</p> : data.contrapartes.map((c) => (
                <label key={c.alegra_id} className={`mr-2 inline-flex cursor-pointer items-center gap-1 rounded border px-2 py-1 text-xs ${contraparte?.alegra_id === c.alegra_id ? 'border-violet-400 bg-violet-50' : ''}`}>
                  <input type="radio" name="contraparte" checked={contraparte?.alegra_id === c.alegra_id} onChange={() => setContraparte(c)} data-testid={`factoring-contraparte-${c.alegra_id}`} /> {c.nombre} <span className="text-muted-foreground">{c.identificacion}</span>
                </label>
              ))}
            </div>
            <div>
              <p className="mb-1 text-xs font-medium">Pago Alegra existente que respalda el anticipo (grupo {sel.length} ↔ 1)</p>
              {data.pagos_alegra.length === 0 ? <p className="text-xs text-amber-700">No hay un pago en Alegra que calce con el total ni mencione factoring. Regístralo primero en Alegra (pago de la factura cedida) y vuelve a intentar.</p> : (
                <div className="space-y-1" data-testid="factoring-pagos">
                  {data.pagos_alegra.map((p) => (
                    <label key={p.alegra_id} className={`flex cursor-pointer items-start gap-2 rounded border p-1.5 text-xs ${p.ya_usado ? 'opacity-50' : ''} ${pagoSel === p.alegra_id ? 'border-violet-400 bg-violet-50' : ''}`}>
                      <input type="radio" name="pago" disabled={p.ya_usado} checked={pagoSel === p.alegra_id} onChange={() => setPagoSel(p.alegra_id)} data-testid={`factoring-pago-${p.alegra_id}`} className="mt-0.5" />
                      <span className="flex-1">#{p.alegra_id} · {fmtFecha(p.fecha)} · {p.cliente_nombre} · <span className="text-muted-foreground">{p.observaciones}</span>
                        {(p.facturas || []).length > 0 && <span className="block text-muted-foreground">Factura(s): {p.facturas.map((f) => `${f.number} (anticipo $${CLP(f.amount)} de $${CLP(f.total)})`).join(', ')}</span>}
                        {p.ya_usado && <span className="block text-amber-700">ya respalda otros depósitos</span>}
                      </span>
                      <span className={`font-mono ${p.calza_total ? 'text-emerald-700' : 'text-red-600'}`}>${CLP(p.monto)}{p.calza_total ? ' ✓' : ' ≠'}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <Textarea value={nota} onChange={(e) => setNota(e.target.value)} rows={2} placeholder="Nota (ej: operación X Capital, factura 2523 MASISA; liquidación pendiente)" data-testid="factoring-nota" />
            {pago && <p className="rounded bg-violet-50 px-2 py-1.5 text-[11px] text-violet-900" data-testid="factoring-resumen">Se conciliarán {sel.length} depósitos (${CLP(total)}) como <b>factoring {contraparte?.nombre || ''}</b> contra el pago Alegra #{pago.alegra_id}. No se crea nada nuevo en Alegra. La liquidación (intereses/excedente) se registra después, cuando llegue la cartola con el cierre.</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className="bg-violet-600 hover:bg-violet-700" disabled={guardando || !data || sel.length === 0 || !pagoSel || !contraparte || (pago && !pago.calza_total)} onClick={confirmar} data-testid="btn-confirmar-factoring">
            {guardando ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1 h-4 w-4" />} Conciliar como factoring
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
